import { PDFDocument, rgb, StandardFonts, degrees } from 'pdf-lib';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface PayslipData {
  staffName: string;
  staffEmail: string;
  staffRole: string;
  panNumber?: string;
  bankAccountNumber?: string;
  bankIfscCode?: string;
  month: number;
  year: number;
  workingDays: number;
  daysWorked: number;
  baseSalary: number;
  basicPay: number;
  hraPay: number;
  otherAllowances: number;
  grossPay: number;
  customAllowances: { label: string; amount: number }[];
  customDeductions: { label: string; amount: number }[];
  totalAllowances: number;
  totalDeductions: number;
  netPay: number;
  status: 'draft' | 'paid';
  paidDate?: string;
}

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const fmtCurrency = (n: number) => {
  const formatted = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
  }).format(n);
  return formatted.replace(/₹/g, 'Rs. ');
};

// ─── PDF Generator ────────────────────────────────────────────────────────────
export async function generatePayslipPDF(
  data: PayslipData,
  companyName: string,
  companyAddress?: string,
  companyEmail?: string,
  companyPhone?: string,
  logoUrl?: string,
  primaryColor?: string,
): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.create();
  const page = pdfDoc.addPage([595, 842]); // A4
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const W = page.getWidth();
  const H = page.getHeight();
  const M = 45; // margin
  const LH = 16; // line height
  const contentW = W - M * 2;

  // Colors
  const parseHex = (hex: string) => {
    const c = (hex || '#0891B2').replace('#', '');
    return rgb(
      parseInt(c.substring(0, 2), 16) / 255,
      parseInt(c.substring(2, 4), 16) / 255,
      parseInt(c.substring(4, 6), 16) / 255,
    );
  };
  const brand = parseHex(primaryColor || '#0891B2');
  const darkText = rgb(0.12, 0.12, 0.12);
  const grayText = rgb(0.4, 0.4, 0.4);
  const lightGray = rgb(0.92, 0.92, 0.92);
  const white = rgb(1, 1, 1);
  const greenColor = rgb(0.02, 0.59, 0.41);
  const redColor = rgb(0.86, 0.15, 0.15);

  let y = H - M;

  // ── Top Brand Bar ──────────────────────────────────────────────────────────
  page.drawRectangle({ x: 0, y: H - 8, width: W, height: 8, color: brand });

  // ── Company Header ─────────────────────────────────────────────────────────
  // Logo
  if (logoUrl) {
    try {
      const base64 = logoUrl.split(',')[1];
      let img;
      if (logoUrl.includes('image/png')) img = await pdfDoc.embedPng(base64);
      else if (logoUrl.includes('image/jpeg') || logoUrl.includes('image/jpg')) img = await pdfDoc.embedJpg(base64);
      if (img) {
        const lw = 80;
        const lh = (img.height / img.width) * lw;
        page.drawImage(img, { x: W - M - lw, y: y - lh + 10, width: lw, height: lh });
      }
    } catch { /* skip logo errors */ }
  }

  page.drawText(companyName || 'Company', { x: M, y, size: 16, font: bold, color: brand });
  y -= LH;
  if (companyAddress) { page.drawText(companyAddress, { x: M, y, size: 9, font, color: grayText }); y -= LH * 0.8; }
  if (companyEmail) { page.drawText(`Email: ${companyEmail}`, { x: M, y, size: 9, font, color: grayText }); y -= LH * 0.8; }
  if (companyPhone) { page.drawText(`Phone: ${companyPhone}`, { x: M, y, size: 9, font, color: grayText }); y -= LH * 0.8; }

  y -= LH;

  // ── Title Bar ──────────────────────────────────────────────────────────────
  page.drawRectangle({ x: M, y: y - 5, width: contentW, height: 30, color: brand });
  page.drawText('PAYSLIP', { x: M + 10, y: y + 3, size: 16, font: bold, color: white });
  const periodText = `${MONTHS[data.month - 1]} ${data.year}`;
  const periodW = bold.widthOfTextAtSize(periodText, 12);
  page.drawText(periodText, { x: W - M - periodW - 10, y: y + 5, size: 12, font: bold, color: white });
  y -= 45;

  // ── Employee Details Box ───────────────────────────────────────────────────
  page.drawRectangle({ x: M, y: y - 78, width: contentW, height: 85, color: rgb(0.97, 0.97, 0.99), borderColor: lightGray, borderWidth: 0.5 });

  const col1 = M + 12;
  const col2 = M + contentW / 2 + 10;
  const valOffset = 110;

  const drawField = (x: number, yy: number, label: string, value: string) => {
    page.drawText(label, { x, y: yy, size: 8.5, font, color: grayText });
    page.drawText(value || 'N/A', { x: x + valOffset, y: yy, size: 9, font: bold, color: darkText });
  };

  drawField(col1, y, 'Employee Name:', data.staffName);
  drawField(col2, y, 'Employee Email:', data.staffEmail);
  y -= LH;
  drawField(col1, y, 'Designation:', data.staffRole);
  drawField(col2, y, 'PAN:', data.panNumber || 'N/A');
  y -= LH;
  drawField(col1, y, 'Working Days:', `${data.workingDays}`);
  drawField(col2, y, 'Days Worked:', `${data.daysWorked}`);
  y -= LH;
  drawField(col1, y, 'Bank A/C:', data.bankAccountNumber || 'N/A');
  drawField(col2, y, 'IFSC:', data.bankIfscCode || 'N/A');

  y -= LH * 2;

  // ── Earnings & Deductions Tables Side by Side ──────────────────────────────
  const tableW = (contentW - 15) / 2;
  const tableLeft = M;
  const tableRight = M + tableW + 15;
  const headerH = 22;
  const rowH = LH + 2;

  // Earnings items
  const earningsItems = [
    { label: 'Basic Salary (50%)', amount: data.basicPay },
    { label: 'HRA (25%)', amount: data.hraPay },
    { label: 'Other Allowances (25%)', amount: data.otherAllowances },
    ...data.customAllowances,
  ];
  const earningsTotal = data.grossPay + data.totalAllowances;

  // Deductions items
  const deductionItems = [...data.customDeductions];
  const deductionsTotal = data.totalDeductions;

  const maxRows = Math.max(earningsItems.length, deductionItems.length, 1);
  const tableH = headerH + maxRows * rowH + rowH; // +1 for total row

  // ── EARNINGS TABLE ──
  // Header
  page.drawRectangle({ x: tableLeft, y: y - headerH + 6, width: tableW, height: headerH, color: rgb(0.02, 0.59, 0.41) });
  page.drawText('EARNINGS', { x: tableLeft + 8, y: y - 8, size: 9, font: bold, color: white });
  page.drawText('Amount', { x: tableLeft + tableW - 65, y: y - 8, size: 9, font: bold, color: white });

  let ey = y - headerH - 5;
  earningsItems.forEach((item, i) => {
    if (i % 2 === 0) {
      page.drawRectangle({ x: tableLeft, y: ey - rowH + LH + 2, width: tableW, height: rowH, color: rgb(0.98, 0.98, 0.98) });
    }
    page.drawText(item.label, { x: tableLeft + 8, y: ey, size: 8.5, font, color: darkText });
    page.drawText(fmtCurrency(item.amount), { x: tableLeft + tableW - 85, y: ey, size: 8.5, font, color: darkText });
    ey -= rowH;
  });
  // Pad empty rows
  for (let i = earningsItems.length; i < maxRows; i++) { ey -= rowH; }
  // Total row
  page.drawRectangle({ x: tableLeft, y: ey - rowH + LH + 2, width: tableW, height: rowH, color: rgb(0.93, 0.98, 0.95) });
  page.drawText('Total Earnings', { x: tableLeft + 8, y: ey, size: 9, font: bold, color: greenColor });
  page.drawText(fmtCurrency(earningsTotal), { x: tableLeft + tableW - 85, y: ey, size: 9, font: bold, color: greenColor });

  // ── DEDUCTIONS TABLE ──
  // Header
  page.drawRectangle({ x: tableRight, y: y - headerH + 6, width: tableW, height: headerH, color: redColor });
  page.drawText('DEDUCTIONS', { x: tableRight + 8, y: y - 8, size: 9, font: bold, color: white });
  page.drawText('Amount', { x: tableRight + tableW - 65, y: y - 8, size: 9, font: bold, color: white });

  let dy = y - headerH - 5;
  if (deductionItems.length === 0) {
    page.drawText('No deductions', { x: tableRight + 8, y: dy, size: 8.5, font, color: grayText });
    dy -= rowH;
  } else {
    deductionItems.forEach((item, i) => {
      if (i % 2 === 0) {
        page.drawRectangle({ x: tableRight, y: dy - rowH + LH + 2, width: tableW, height: rowH, color: rgb(0.98, 0.98, 0.98) });
      }
      page.drawText(item.label, { x: tableRight + 8, y: dy, size: 8.5, font, color: darkText });
      page.drawText(fmtCurrency(item.amount), { x: tableRight + tableW - 85, y: dy, size: 8.5, font, color: darkText });
      dy -= rowH;
    });
  }
  // Pad empty rows
  for (let i = deductionItems.length; i < maxRows; i++) { dy -= rowH; }
  // Total row
  page.drawRectangle({ x: tableRight, y: dy - rowH + LH + 2, width: tableW, height: rowH, color: rgb(0.99, 0.94, 0.94) });
  page.drawText('Total Deductions', { x: tableRight + 8, y: dy, size: 9, font: bold, color: redColor });
  page.drawText(fmtCurrency(deductionsTotal), { x: tableRight + tableW - 85, y: dy, size: 9, font: bold, color: redColor });

  y = Math.min(ey, dy) - rowH - 15;

  // ── Net Pay Box ────────────────────────────────────────────────────────────
  page.drawRectangle({ x: M, y: y - 42, width: contentW, height: 48, color: brand, borderColor: brand, borderWidth: 1 });
  page.drawText('NET PAYABLE SALARY', { x: M + 15, y: y - 10, size: 10, font, color: white });
  const netText = fmtCurrency(data.netPay);
  const netW = bold.widthOfTextAtSize(netText, 20);
  page.drawText(netText, { x: W - M - netW - 15, y: y - 16, size: 20, font: bold, color: white });
  const periodSubText = `For ${MONTHS[data.month - 1]} ${data.year} (${data.daysWorked}/${data.workingDays} days)`;
  page.drawText(periodSubText, { x: M + 15, y: y - 28, size: 8, font, color: rgb(1, 1, 1) });

  y -= 65;

  // ── PAID watermark ─────────────────────────────────────────────────────────
  if (data.status === 'paid') {
    page.drawText('PAID', {
      x: W / 2 - 80,
      y: H / 2 - 30,
      size: 80,
      font: bold,
      color: rgb(0.02, 0.59, 0.41),
      opacity: 0.08,
      rotate: degrees(35),
    });
    if (data.paidDate) {
      page.drawText(`Paid on: ${data.paidDate}`, { x: M, y: y, size: 9, font: bold, color: greenColor });
      y -= LH;
    }
  }

  // ── Bank Details Section ───────────────────────────────────────────────────
  if (data.bankAccountNumber) {
    y -= 5;
    page.drawText('BANK TRANSFER DETAILS', { x: M, y, size: 9, font: bold, color: brand });
    y -= LH;
    page.drawRectangle({ x: M, y: y - 42, width: contentW, height: 48, color: rgb(0.97, 0.97, 0.99), borderColor: lightGray, borderWidth: 0.5 });
    drawField(col1, y - 5, 'Account No:', data.bankAccountNumber);
    drawField(col2, y - 5, 'IFSC Code:', data.bankIfscCode || 'N/A');
    drawField(col1, y - 5 - LH, 'Account Name:', data.staffName);
    y -= 60;
  }

  // ── Footer ─────────────────────────────────────────────────────────────────
  const footerY = M + LH * 2;
  page.drawLine({ start: { x: M, y: footerY + LH }, end: { x: W - M, y: footerY + LH }, thickness: 0.5, color: lightGray });
  page.drawText('This is a system-generated payslip and does not require a signature.', {
    x: W / 2 - 170,
    y: footerY,
    size: 8,
    font,
    color: grayText,
  });
  page.drawText(`Generated by AkMo Books on ${new Date().toLocaleDateString('en-IN')}`, {
    x: W / 2 - 110,
    y: footerY - LH * 0.8,
    size: 7,
    font,
    color: grayText,
  });

  // Bottom brand bar
  page.drawRectangle({ x: 0, y: 0, width: W, height: 4, color: brand });

  const pdfBytes = await pdfDoc.save();
  return new Uint8Array(pdfBytes);
}
